import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  FileCopy as LinkIcon,
} from '@mui/icons-material';
import LabCatalogContent from './LabCatalogContent';

const CompareTestsModal = ({
  open,
  onClose,
  allTests,
  onAddTest,
  initialTest,
}) => {
  const [comparedTests, setComparedTests] = useState([]);
  const [copied, setCopied] = useState(false);
  const [addTestModalOpen, setAddTestModalOpen] = useState(false);

  // Load from URL params if available
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const testIds = params.get('compare');
    if (testIds && open && allTests.length > 0) {
      const ids = testIds.split(',');
      const tests = allTests.filter(t => ids.includes(t._id));
      if (tests.length > 0) {
        setComparedTests(tests);
      }
    }
  }, [open, allTests]);

  // Auto-add initial test when modal opens from test details
  useEffect(() => {
    if (open && initialTest && initialTest._id) {
      setComparedTests((previousTests) => {
        // Only add if not already in comparison
        if (previousTests.find((t) => t._id === initialTest._id)) {
          return previousTests;
        }
        return [initialTest];
      });
    }
  }, [open, initialTest]);


  const handleAddTest = () => {
    setAddTestModalOpen(true);
  };

  const handleSelectTestToCompare = (test) => {
    if (comparedTests.length < 4) {
      setComparedTests([...comparedTests, test]);
      setAddTestModalOpen(false);
    }
  };

  const handleRemoveTest = (testId) => {
    setComparedTests(comparedTests.filter(t => t._id !== testId));
  };

  const copyToClipboard = () => {
    const testIds = comparedTests.map(t => t._id).join(',');
    const url = `${window.location.origin}/lab-catalog?compare=${testIds}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getSampleTypeColor = (sampleTypeName) => {
    const name = sampleTypeName?.toLowerCase() || '';
    if (name.includes('stool')) return '#FF6B35';
    if (name.includes('serum')) return '#E63946';
    if (name.includes('blood')) return '#D62828';
    if (name.includes('saliva')) return '#00BBF9';
    if (name.includes('urine')) return '#F77F00';
    return '#6C757D';
  };

  const comparisonRows = [
    {
      label: 'Additional Fees',
      icon: '📋',
      key: 'fees',
      render: (test) => test.additionalFees || '--',
    },
    {
      label: 'Sample Type',
      icon: '🧪',
      key: 'sampleType',
      render: (test) =>
        test.sampleType ? (
          `<span style="background-color: ${getSampleTypeColor(test.sampleType.name)}; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold; font-size: 12px;">${test.sampleType.name.toUpperCase()}</span>`
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Phlebotomy',
      icon: '🩸',
      key: 'phlebotomy',
      render: (test) => test.phlebotomy || '--',
    },
    {
      label: 'Shipping Time',
      icon: '🚚',
      key: 'shippingTime',
      render: (test) =>
        test.shippingTimeMin && test.shippingTimeMax
          ? `${test.shippingTimeMin}-${test.shippingTimeMax} Business Days`
          : '--',
    },
    {
      label: 'Processing Time',
      icon: '⏱️',
      key: 'processingTime',
      render: (test) =>
        test.processingTime
          ? `${test.processingTime} Business Days`
          : '--',
    },
    {
      label: 'Sample Report',
      icon: '📄',
      key: 'sampleReport',
      render: (test) =>
        test.sampleReport
          ? `<a href="${test.sampleReport}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none;">View Report</a>`
          : '--',
    },
    {
      label: 'Collection Instructions',
      icon: '📋',
      key: 'collectionInstructions',
      render: (test) =>
        test.collectionInstructions
          ? `<a href="${test.collectionInstructions}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none;">View Instructions</a>`
          : '--',
    },
    {
      label: 'Location',
      icon: '📍',
      key: 'location',
      render: (test) => test.location?.name || 'No Restrictions',
    },
    {
      label: 'Category',
      icon: '📂',
      key: 'category',
      render: (test) => test.category?.name || 'N/A',
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            maxHeight: '95vh',
            zIndex: 1300,
          },
        }}
      >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #e8e8e8',
          pb: 2,
          backgroundColor: '#f8f9fa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon sx={{ fontSize: 28, color: '#1976d2' }} />
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              fontSize: '1.25rem',
            }}
          >
            Compare Lab Tests
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {comparedTests.length > 0 && (
            <Tooltip title={copied ? 'Copied!' : 'Copy link to clipboard'}>
              <Button
                variant="outlined"
                size="small"
                onClick={copyToClipboard}
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                sx={{
                  color: copied ? '#4caf50' : '#1976d2',
                  borderColor: copied ? '#4caf50' : '#1976d2',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: copied
                      ? 'rgba(76, 175, 80, 0.04)'
                      : 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Copy Link to Comparison
              </Button>
            </Tooltip>
          )}
          <IconButton
            onClick={() => onClose(comparedTests)}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        {/* Test Selection Cards */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {comparedTests.map((test) => (
              <Grid item xs={12} sm={6} md={3} key={test._id}>
                <Card
                  sx={{
                    position: 'relative',
                    border: '2px solid #1976d2',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveTest(test._id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#d32f2f',
                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.2)',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        mb: 1,
                        pr: 3,
                        fontSize: '0.95rem',
                      }}
                    >
                      {test.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        fontSize: '0.85rem',
                      }}
                    >
                      {test.lab?.name || 'Unknown Lab'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {comparedTests.length < 4 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  onClick={handleAddTest}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 140,
                    backgroundColor: '#e3f2fd',
                    border: '2px dashed #1976d2',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <AddIcon
                      sx={{
                        fontSize: 40,
                        color: '#1976d2',
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#1976d2',
                        fontWeight: 600,
                      }}
                    >
                      Add Test to Comparison
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

        {comparedTests.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No tests selected. Click the button above to add tests to compare.
            </Typography>
          </Alert>
        ) : (
          <>
            {/* Comparison Table Header */}
            <Box
              sx={{
                backgroundColor: '#00897b',
                color: 'white',
                p: 2,
                borderRadius: '4px 4px 0 0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                📊 LAB TEST INFORMATION
              </Typography>
            </Box>

            {/* Comparison Table */}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '0 0 4px 4px',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: '#666',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px',
                        width: '180px',
                        borderRight: '1px solid #e8e8e8',
                      }}
                    >
                      Attribute
                    </TableCell>
                    {comparedTests.map((test) => (
                      <TableCell
                        key={test._id}
                        align="center"
                        sx={{
                          fontWeight: 700,
                          color: '#1976d2',
                          fontSize: '0.85rem',
                          borderRight: '1px solid #e8e8e8',
                        }}
                      >
                        {test.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonRows.map((row, idx) => (
                    <TableRow
                      key={row.key}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                        borderBottom: '1px solid #e8e8e8',
                        '&:hover': {
                          backgroundColor: idx % 2 === 0 ? '#f5f5f5' : '#f0f0f0',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: '#666',
                          fontSize: '0.9rem',
                          borderRight: '1px solid #e8e8e8',
                          width: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                        }}
                      >
                        <span>{row.icon}</span>
                        {row.label}
                      </TableCell>
                      {comparedTests.map((test) => (
                        <TableCell
                          key={test._id}
                          align="center"
                          sx={{
                            fontSize: '0.9rem',
                            color: '#333',
                            borderRight: '1px solid #e8e8e8',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: row.render(test),
                          }}
                        />
                      ))}
                    </TableRow>
                  ))}

                  {/* Biomarkers Row */}
                  {comparedTests.some(t => t.biomarkers && t.biomarkers.length > 0) && (
                    <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: '#666',
                          fontSize: '0.9rem',
                          borderRight: '1px solid #e8e8e8',
                          width: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          verticalAlign: 'top',
                          pt: 2,
                        }}
                      >
                        <span>🧬</span>
                        Biomarkers
                      </TableCell>
                      {comparedTests.map((test) => (
                        <TableCell
                          key={test._id}
                          align="center"
                          sx={{
                            fontSize: '0.85rem',
                            color: '#333',
                            borderRight: '1px solid #e8e8e8',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          {test.biomarkers && test.biomarkers.length > 0 ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                alignItems: 'center',
                              }}
                            >
                              {test.biomarkers.slice(0, 5).map((biomarker, idx) => (
                                <Chip
                                  key={idx}
                                  label={biomarker}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              ))}
                              {test.biomarkers.length > 5 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#999', fontStyle: 'italic' }}
                                >
                                  +{test.biomarkers.length - 5} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            '--'
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      </Dialog>

      {/* Add Test to Comparison Modal */}
      <Dialog
        open={addTestModalOpen}
        onClose={() => setAddTestModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            maxHeight: '95vh',
            zIndex: 1400,
          },
        }}
      >
        {/* Header with Title and Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
            }}
          >
            Search for a Lab Test to Compare
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setAddTestModalOpen(false)}
              sx={{
                cursor: 'pointer',
                color: '#1976d2',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              See Comparison →
            </Link>
            <IconButton
              onClick={() => setAddTestModalOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Use LabCatalogContent component with select button mode */}
          <LabCatalogContent 
            showSelectButton={true}
            onSelectTest={handleSelectTestToCompare}
            selectedTestIds={comparedTests.map(t => t._id)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompareTestsModal;

